import type { PickerStream } from "./picker-stream";

export function formatHeight(heightM: number): string {
    if (Math.abs(heightM) < 1) return `${Math.round(heightM * 100)} cm`;
    return `${Number(heightM.toFixed(1))} m`;
}

export function pickerStreamMeasurement(stream: PickerStream): string {
    const nameIsInformative = !stream.name.toLowerCase().includes(stream.unitCategoryName.toLowerCase());
    return nameIsInformative ? stream.name : stream.unitCategoryName;
}

export function pickerStreamDetail(stream: PickerStream, withHeight: boolean): string {
    return [stream.hostName, withHeight && stream.heightM !== null ? formatHeight(stream.heightM) : null]
        .filter((part) => !!part)
        .join(" · ");
}

export interface PickerRowText {
    measurement: string;
    detail: string;
}

/** Streams that would read the same on one device, grouped by what that row would say. */
function sameRowText(
    streams: readonly PickerStream[],
    measurementOf: (stream: PickerStream) => string,
): PickerStream[][] {
    const byRowText = new Map<string, PickerStream[]>();
    streams.forEach((stream) => {
        const key = [stream.unitCategory, stream.hostName, measurementOf(stream)].join("\u0000");
        byRowText.set(key, [...(byRowText.get(key) ?? []), stream]);
    });
    return [...byRowText.values()];
}

/** Each row's text. The full name and the height go in only where two rows would read the same without them. */
export function pickerRowTexts(streams: readonly PickerStream[]): ReadonlyMap<number, PickerRowText> {
    const fullName = new Set<number>();
    sameRowText(streams, pickerStreamMeasurement).forEach((group) => {
        if (new Set(group.map((stream) => stream.name)).size > 1) group.forEach((stream) => fullName.add(stream.id));
    });
    const measurementOf = (stream: PickerStream) =>
        fullName.has(stream.id) ? stream.name : pickerStreamMeasurement(stream);

    const withHeight = new Set<number>();
    sameRowText(streams, measurementOf).forEach((group) => {
        if (new Set(group.map((stream) => stream.heightM)).size > 1)
            group.forEach((stream) => withHeight.add(stream.id));
    });

    return new Map(
        streams.map((stream) => [
            stream.id,
            { measurement: measurementOf(stream), detail: pickerStreamDetail(stream, withHeight.has(stream.id)) },
        ]),
    );
}
